import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import PropTypes from 'prop-types';

import OntraccrButton from '../../common/buttons/OnTraccrButton';
import DraggableTable from '../../projects/ProjectScheduleOfValues/DraggableTable';
import SimpleTextInputModal from '../../common/modals/SimpleTextInputModal';
import {
  createShortListCategory,
  updateShortListCategory,
  deleteShortListCategory,
  reorderShortListCategories,
} from '../state/boards.actions';
import BorderlessButton from '../../common/buttons/BorderlessButton';
import CustomConfirmModal from '../../common/modals/CustomConfirmModal';
import useToggle from '../../common/hooks/useToggle';

export default function ShortListCategoryTab({
  shortList,
}) {
  const dispatch = useDispatch();
  const shortListCategoryMap = useSelector((state) => state.boards.shortListCategoryMap);

  const [selectedCategory, setSelectedCategory] = useState();
  const { toggle: toggleCategory, isToggled: isToggledCategory } = useToggle();

  const onToggleCategory = useCallback((record) => {
    toggleCategory();
    setSelectedCategory(record);
  }, [toggleCategory]);

  const onUpsertCategory = useCallback(async (name) => {
    const res = selectedCategory
      ? await dispatch(updateShortListCategory(shortList.id, selectedCategory.id, { name }))
      : await dispatch(createShortListCategory(shortList.id, { name }));
    if (res) onToggleCategory();
  }, [shortList, selectedCategory, onToggleCategory]);

  const onDeleteCategory = useCallback((record) => {
    CustomConfirmModal({
      title: 'Delete Category',
      content: 'Are you sure you want to delete this category?',
      onOk: () => dispatch(deleteShortListCategory(shortList.id, record.id)),
      zIndex: 5000,
    });
  }, [shortList, onToggleCategory]);

  const relevantCategories = useMemo(() => (
    shortListCategoryMap?.[shortList.id] || []
  ), [shortListCategoryMap, shortList]);

  const columns = useMemo(() => [
    {
      title: 'Name',
      dataIndex: 'name',
      ellipsis: true,
      width: 150,
    },
    {
      title: '',
      dataIndex: 'edit',
      width: 30,
      render: (_, record) => (
        <BorderlessButton
          iconNode={<EditOutlined />}
          onClick={() => onToggleCategory(record)}
        />
      ),
    },
    {
      title: '',
      dataIndex: 'delete',
      width: 30,
      render: (_, record) => (
        <BorderlessButton
          iconNode={<DeleteOutlined style={{ color: 'red' }} />}
          onClick={() => onDeleteCategory(record)}
        />
      ),
    },
  ], [onDeleteCategory]);

  const onSetData = useCallback((_, data) => {
    const payload = data.map((item, index) => ({
      id: item.id,
      orderIndex: index,
    }));
    dispatch(reorderShortListCategories(shortList.id, { categories: payload }));
  }, [shortList]);

  return (
    <DndProvider backend={HTML5Backend}>
      <OntraccrButton
        title="Add Category"
        onClick={() => onToggleCategory()}
      />
      <DraggableTable
        dataSource={relevantCategories}
        columns={columns}
        size="small"
        key="id"
        pagination={false}
        isDraggable
        setData={onSetData}
      />
      <SimpleTextInputModal
        title={`${selectedCategory ? 'Edit' : 'Add'} Category`}
        visible={isToggledCategory}
        onClose={() => onToggleCategory()}
        onSave={onUpsertCategory}
        placeholder="Enter Category Name"
        initialValue={selectedCategory?.name}
      />
    </DndProvider>
  );
}

ShortListCategoryTab.propTypes = {
  shortList: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
};
